import { AnimatePresence, motion } from "framer-motion";
import { Info, Keyboard, Monitor, Moon, Sun, Type, Volume2, VolumeX, X } from "lucide-react";
import { cn } from "../lib/utils";

type Theme = "dark" | "light" | "system";

interface SettingsPanelProps {
	open: boolean;
	onClose: () => void;
	theme: Theme;
	onThemeChange: (t: Theme) => void;
	soundEnabled: boolean;
	onSoundChange: (v: boolean) => void;
	fontSize: number;
	onFontSizeChange: (v: number) => void;
}

export function SettingsPanel({
	open,
	onClose,
	theme,
	onThemeChange,
	soundEnabled,
	onSoundChange,
	fontSize,
	onFontSizeChange,
}: SettingsPanelProps) {
	const themes: { value: Theme; label: string; icon: React.ReactNode }[] = [
		{ value: "dark", label: "Dark", icon: <Moon className="w-4 h-4" /> },
		{ value: "light", label: "Light", icon: <Sun className="w-4 h-4" /> },
		{ value: "system", label: "System", icon: <Monitor className="w-4 h-4" /> },
	];

	return (
		<AnimatePresence>
			{open && (
				<>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black/50 z-40"
						onClick={onClose}
					/>
					<motion.div
						initial={{ opacity: 0, x: 300 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: 300 }}
						transition={{ type: "spring", damping: 25, stiffness: 200 }}
						className="fixed inset-y-0 right-0 w-full max-w-sm bg-card border-l border-border z-50 flex flex-col shadow-2xl"
					>
						<div className="flex items-center justify-between px-4 py-3 border-b border-border">
							<h2 className="font-semibold">Settings</h2>
							<button
								type="button"
								onClick={onClose}
								className="p-2 rounded-lg hover:bg-secondary transition-colors"
							>
								<X className="w-4 h-4" />
							</button>
						</div>

						<div className="flex-1 overflow-y-auto p-4 space-y-6">
							{/* Theme */}
							<div className="space-y-3">
								<div className="text-sm font-medium flex items-center gap-2">
									<Sun className="w-4 h-4 text-muted-foreground" />
									Theme
								</div>
								<div className="grid grid-cols-3 gap-2">
									{themes.map((t) => (
										<button
											key={t.value}
											type="button"
											onClick={() => onThemeChange(t.value)}
											className={cn(
												"flex flex-col items-center gap-1.5 p-3 rounded-xl border text-sm transition-all",
												theme === t.value
													? "border-primary bg-primary/10 text-primary"
													: "border-border hover:border-primary/30"
											)}
										>
											{t.icon}
											{t.label}
										</button>
									))}
								</div>
							</div>

							{/* Font Size */}
							<div className="space-y-3">
								<div className="text-sm font-medium flex items-center gap-2">
									<Type className="w-4 h-4 text-muted-foreground" />
									Font Size
								</div>
								<div className="flex items-center gap-3 px-1">
									<span className="text-xs text-muted-foreground">A</span>
									<input
										type="range"
										min={14}
										max={20}
										value={fontSize}
										onChange={(e) => onFontSizeChange(Number(e.target.value))}
										className="flex-1 h-1.5 rounded-full bg-secondary appearance-none cursor-pointer accent-primary"
									/>
									<span className="text-sm text-muted-foreground">A</span>
								</div>
								<p className="text-xs text-muted-foreground px-1">{fontSize}px</p>
							</div>

							{/* Sound */}
							<div className="space-y-3">
								<div className="text-sm font-medium flex items-center gap-2">
									{soundEnabled ? (
										<Volume2 className="w-4 h-4 text-muted-foreground" />
									) : (
										<VolumeX className="w-4 h-4 text-muted-foreground" />
									)}
									Sound Effects
								</div>
								<button
									type="button"
									onClick={() => onSoundChange(!soundEnabled)}
									className={cn(
										"w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm transition-all",
										soundEnabled
											? "border-primary bg-primary/10 text-primary"
											: "border-border hover:border-primary/30"
									)}
								>
									<span>{soundEnabled ? "Enabled" : "Disabled"}</span>
									<div
										className={cn(
											"w-9 h-5 rounded-full transition-colors relative",
											soundEnabled ? "bg-primary" : "bg-secondary"
										)}
									>
										<div
											className={cn(
												"absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform",
												soundEnabled ? "translate-x-4.5 left-0.5" : "left-0.5"
											)}
										/>
									</div>
								</button>
							</div>

							{/* Keyboard Shortcuts */}
							<div className="space-y-3">
								<div className="text-sm font-medium flex items-center gap-2">
									<Keyboard className="w-4 h-4 text-muted-foreground" />
									Keyboard Shortcuts
								</div>
								<div className="space-y-2 text-sm">
									<div className="flex items-center justify-between py-2 px-3 rounded-lg bg-secondary/50">
										<span>New chat</span>
										<kbd className="px-1.5 py-0.5 rounded text-xs bg-secondary font-mono">⌘ N</kbd>
									</div>
									<div className="flex items-center justify-between py-2 px-3 rounded-lg bg-secondary/50">
										<span>Focus input</span>
										<kbd className="px-1.5 py-0.5 rounded text-xs bg-secondary font-mono">⌘ K</kbd>
									</div>
									<div className="flex items-center justify-between py-2 px-3 rounded-lg bg-secondary/50">
										<span>Toggle sidebar</span>
										<kbd className="px-1.5 py-0.5 rounded text-xs bg-secondary font-mono">⌘ B</kbd>
									</div>
									<div className="flex items-center justify-between py-2 px-3 rounded-lg bg-secondary/50">
										<span>Send message</span>
										<kbd className="px-1.5 py-0.5 rounded text-xs bg-secondary font-mono">↵</kbd>
									</div>
									<div className="flex items-center justify-between py-2 px-3 rounded-lg bg-secondary/50">
										<span>New line</span>
										<kbd className="px-1.5 py-0.5 rounded text-xs bg-secondary font-mono">⇧ ↵</kbd>
									</div>
								</div>
							</div>

							{/* About */}
							<div className="space-y-3">
								<div className="text-sm font-medium flex items-center gap-2">
									<Info className="w-4 h-4 text-muted-foreground" />
									About
								</div>
								<div className="text-sm text-muted-foreground space-y-1 px-3">
									<p>Boring Agent v1.0.0</p>
									<p>Built on Cloudflare&apos;s edge network</p>
									<p>AI SDK + Durable Objects + Workers AI</p>
								</div>
							</div>
						</div>
					</motion.div>
				</>
			)}
		</AnimatePresence>
	);
}
